const DB_NAME = "burton-bikes-local-content";
const DB_VERSION = 1;
const STORE_NAME = "content";

interface StoredValue {
  key: string;
  value: string;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error("IndexedDB open blocked"));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDatabase();
  return await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const request = operation(store);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
    tx.onabort = () => reject(tx.error);
  });
}

export async function getStoredContent(): Promise<string | null> {
  if (typeof window === "undefined" || !("indexedDB" in window)) return null;

  try {
    const result = await withStore(
      "readonly",
      (store) => store.get("content") as IDBRequest<StoredValue | undefined>,
    );
    return result?.value ?? null;
  } catch {
    return null;
  }
}

export async function setStoredContent(value: string): Promise<void> {
  if (typeof window === "undefined" || !("indexedDB" in window)) return;

  await withStore("readwrite", (store) =>
    store.put({ key: "content", value }) as IDBRequest<IDBValidKey>,
  );
}

export async function removeStoredContent(): Promise<void> {
  if (typeof window === "undefined" || !("indexedDB" in window)) return;

  try {
    await withStore("readwrite", (store) => store.delete("content"));
  } catch {
    // A failed IndexedDB delete should not block falling back to published content.
  }
}
